import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import {
  Brain,
  CircleHelp,
  CornerDownRight,
  Settings2,
  Sparkles,
  ChevronDown,
  Loader2,
  Check,
} from "lucide-react";
import type { ThinkingStep, ThinkingStepKind } from "@/store/slices/aiChatSlice";

interface ThinkingTimelineProps {
  steps: ThinkingStep[];
  /** Le run est-il encore en cours ? Ouvre la timeline et anime l'étape active. */
  isStreaming: boolean;
  /** Durée totale du run (ms), affichée dans l'en-tête une fois terminé. */
  durationMs?: number;
}

const KIND_ICON: Record<ThinkingStepKind, React.ComponentType<{ className?: string }>> = {
  thinking: Brain,
  status: Sparkles,
  question: CircleHelp,
  delegate: CornerDownRight,
  tool: Settings2,
  result: Check,
};

function formatDuration(ms?: number): string | null {
  if (ms == null) return null;
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  return s < 60 ? `${s.toFixed(s < 10 ? 1 : 0)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

const StepRow: React.FC<{ step: ThinkingStep; isLast: boolean }> = ({ step, isLast }) => {
  const Icon = KIND_ICON[step.kind];
  const isThinking = step.kind === "thinking";
  const active = step.status === "active";

  return (
    <li className="relative flex gap-3 pb-3 last:pb-0">
      {/* Ligne verticale connectrice */}
      {!isLast && (
        <span
          aria-hidden
          className="absolute left-[11px] top-6 bottom-0 w-px bg-border"
        />
      )}

      {/* Pastille / icône */}
      <span
        className={cn(
          "relative z-10 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border transition-colors",
          active
            ? "border-primary/40 bg-primary/10 text-primary"
            : "border-border bg-muted text-muted-foreground",
        )}
      >
        {active && !isThinking ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Icon className="h-3 w-3" />
        )}
      </span>

      {/* Contenu */}
      <div className="min-w-0 flex-1 pt-0.5">
        <p
          className={cn(
            "text-xs font-medium leading-snug",
            active ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {step.label}
          {active && isThinking && (
            <span className="ml-1 inline-block h-3 w-1 translate-y-0.5 animate-pulse bg-primary/60" />
          )}
        </p>

        {/* Détail uniquement pour les étapes non-thinking — le raisonnement interne
            de l'agent contient des termes techniques non destinés à l'utilisateur */}
        {step.detail?.trim() && !isThinking && (
          <p className="mt-1 whitespace-pre-wrap break-words text-[11px] italic leading-relaxed text-muted-foreground/70">
            {step.detail.trim()}
          </p>
        )}
      </div>
    </li>
  );
};

export const ThinkingTimeline: React.FC<ThinkingTimelineProps> = ({
  steps,
  isStreaming,
  durationMs,
}) => {
  // Ouvert pendant le stream ; replié automatiquement une fois terminé.
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const wasStreaming = React.useRef(isStreaming);

  React.useEffect(() => {
    // Replie la timeline à la transition stream → terminé.
    if (wasStreaming.current && !isStreaming) setOpen(false);
    wasStreaming.current = isStreaming;
  }, [isStreaming]);

  if (steps.length === 0) return null;

  const duration = formatDuration(durationMs);
  const headerLabel = isStreaming ? t("aiAgent.thinking.inProgress") : t("aiAgent.thinking.label");

  return (
    <div className="mb-2 rounded-xl border border-border/70 bg-muted/30">
      {/* En-tête repliable */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50 cursor-pointer rounded-xl"
        aria-expanded={open}
      >
        <Brain
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-primary",
            isStreaming && "animate-pulse",
          )}
        />
        <span className="text-xs font-medium text-foreground">{headerLabel}</span>
        <span className="text-[10px] tabular-nums text-muted-foreground">
          {steps.length > 1
            ? t("aiAgent.thinking.steps_other", { count: steps.length })
            : t("aiAgent.thinking.steps_one", { count: steps.length })}
          {duration ? ` · ${duration}` : ""}
        </span>
        <ChevronDown
          className={cn(
            "ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>

      {/* Corps */}
      {open && (
        <ol className="px-3 pb-3 pt-1">
          {steps.map((step, i) => (
            <StepRow key={step.id} step={step} isLast={i === steps.length - 1} />
          ))}
        </ol>
      )}
    </div>
  );
};
